import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, CheckCircle2, XCircle, Clock, User, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function ApprovalCenter() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchPending = async () => {
    try {
      const { data } = await API.get("/management/vendors?approval=pending&limit=50");
      setVendors(data.vendors);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPending(); }, []);

  const handleApproval = async () => {
    if (!selected || !action) return;
    setSubmitting(true);
    try {
      await API.put(`/management/vendors/${selected.id}/approval`, {
        approval_status: action, approval_notes: notes
      });
      toast.success(`Technician ${action === "approved" ? "approved" : "put on hold"}`);
      setSelected(null);
      setAction("");
      setNotes("");
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="approval-center">
      <div>
        <h1 className="text-2xl font-heading font-bold text-zinc-900">Approval Center</h1>
        <p className="text-sm text-zinc-500">{vendors.length} technicians pending review</p>
      </div>

      {vendors.length === 0 ? (
        <Card className="border border-zinc-200">
          <CardContent className="p-8 text-center">
            <ShieldCheck className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">No pending approvals</p>
            <p className="text-xs text-zinc-400 mt-1">All technicians have been reviewed</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {vendors.map(v => (
            <Card key={v.id} className="border border-zinc-200 card-hover" data-testid={`pending-technician-${v.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-900">{v.full_name || "No Name"}</h3>
                    <p className="text-xs text-zinc-500">{v.company_name || "No Company"}</p>
                  </div>
                  <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Pending</Badge>
                </div>

                <div className="space-y-1.5 mb-4">
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <span className="font-mono">{v.vendor_code}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <User className="w-3 h-3" /> {v.email_public || "\u2014"}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <Building2 className="w-3 h-3" /> {v.phone_number || "\u2014"}
                  </div>
                  {v.license_number && (
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <ShieldCheck className="w-3 h-3" /> License: {v.license_number}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <Clock className="w-3 h-3" /> Applied {new Date(v.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button size="sm" onClick={() => { setSelected(v); setAction("approved"); }}
                    className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white text-xs"
                    data-testid={`approve-technician-${v.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setSelected(v); setAction("on_hold"); }}
                    className="flex-1 border-rose-200 text-rose-600 hover:bg-rose-50 text-xs"
                    data-testid={`hold-technician-${v.id}`}>
                    <XCircle className="w-3.5 h-3.5 mr-1" /> On Hold
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={() => { setSelected(null); setAction(""); setNotes(""); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {action === "approved" ? "Approve" : "Put On Hold"} Technician
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-600">
                {action === "approved" ? "Approve" : "Put on hold"} <strong>{selected.full_name || selected.vendor_code}</strong>?
              </p>
              <div>
                <label className="text-xs text-zinc-500">Notes (optional)</label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                  placeholder="Add review notes..." className="mt-1" data-testid="approval-notes" />
              </div>
              <Button onClick={handleApproval} disabled={submitting}
                className={`w-full ${action === "approved" ? "bg-zinc-900 hover:bg-zinc-800" : "bg-rose-600 hover:bg-rose-700"} text-white`}
                data-testid="confirm-approval-button">
                {submitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> :
                  action === "approved" ? "Confirm Approval" : "Confirm On Hold"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
